'use client';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as m from 'framer-motion/m';
import { useTheme } from '../lib/ThemeContext';

export const SUB_NAV = [
  { key: 'privacyPolicy', enPath: '/sub/privacy-policy', ltPath: '/lt/sub/privatumo-politika' },
  { key: 'faq', enPath: '/sub/faq', ltPath: '/lt/sub/duk' },
  { key: 'terms', enPath: '/sub/terms-of-service', ltPath: '/lt/sub/naudojimo-taisykles' },
  { key: 'cookies', enPath: '/sub/cookies-info', ltPath: '/lt/sub/slapukai' },
];

interface SubDocsLayoutProps {
  children: React.ReactNode;
}

const SubDocsLayout = ({ children }: SubDocsLayoutProps) => {
  const { theme } = useTheme();
  const { t, lang } = useTranslation('common');
  const router = useRouter();
  const isLt = lang === 'lt';
  const isDarkTheme = theme === 'dark';
  const prefix = router.asPath.startsWith('/lt/') ? '/lt' : router.asPath.startsWith('/us/') ? '/us' : '';

  const getSubPath = (item: (typeof SUB_NAV)[0]) => {
    const base = isLt ? item.ltPath : item.enPath;
    if (prefix === '/us') {
      return base.replace('/sub/', '/us/sub/').replace('/lt/sub/', '/us/sub/');
    }
    return base;
  };

  const currentPath = router.asPath.split('?')[0];

  return (
    <div className={`flex min-h-[70vh] ${isDarkTheme ? 'bg-black' : 'bg-[#e8e8e8]'}`}>
      <aside className={`hidden lg:block w-56 flex-shrink-0 pt-24 pl-6 pr-4 ${isDarkTheme ? 'border-r border-white/15' : 'border-r border-gray-300'}`}>
        <nav className='sticky top-28 space-y-0.5'>
          <p className={`text-xs font-semibold uppercase tracking-wider mb-2 ${isDarkTheme ? 'text-gray-300' : 'text-gray-600'}`}>
            {isLt ? 'Teisinė informacija' : 'Legal'}
          </p>
          <div className={`h-px w-full mb-3 ${isDarkTheme ? 'bg-white/30' : 'bg-gray-500/40'}`} />
          {SUB_NAV.map((item) => {
            const href = getSubPath(item);
            const isActive = currentPath === href || currentPath === href.replace('/us', '');
            const label = item.key === 'privacyPolicy' ? t('footer.privacyPolicy') : t(`footer.${item.key}`);
            return (
              <Link
                key={item.key}
                href={href}
                className={`block py-2 px-3 -ml-3 rounded-lg text-sm transition-colors ${
                  isActive
                    ? isDarkTheme
                      ? 'text-gray-100 font-medium bg-white/10'
                      : 'text-gray-900 font-medium bg-gray-200/80'
                    : isDarkTheme
                      ? 'text-gray-300 hover:text-gray-100'
                      : 'text-gray-700 hover:text-gray-900'
                }`}
              >
                {label}
              </Link>
            );
          })}
        </nav>
      </aside>

      <m.article
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className='flex-1 min-w-0 pt-24 pb-16 px-4 sm:px-8 lg:px-12 max-w-3xl'
      >
        {children}
      </m.article>
    </div>
  );
};

export default SubDocsLayout;
